import { ClockingBase } from "../json-schemas/base/clocking"
import { EmployeeBase } from "../json-schemas/base/employee"
import { SupplierBase } from "../json-schemas/base/supplier"
import { BackofficePlanningEventsCountPerDayResponse } from "../json-schemas/serializers/backoffice-planning-events-count-per-day-response"
import { PlanningGetBetweenResponse } from "../json-schemas/serializers/planning-get-between-response"
import { httpGet, httpGetRoute, httpPost } from "../utils/http.service"
import { moment } from "../utils/moment.service"
import { parseTime } from "../utils/utils.service"
import { scheduleIsScheduleTypeForAnUnavailability } from "./schedules.service"

import type { WorkingDay, MultiOpeningHours } from "./offered-services.service"
import type { StoreProps } from "../stores/planning.store"

export interface PlanningGetBetweenArgs {
  from_date: string
  to_date: string
  include_cancelled_events?: boolean
  includes?: ('marketplaces' | 'employees' | 'customers' | 'offered_service_resources')[]
  marketplace_ids?: number[]
  employee_ids?: number[]
  customer_ids?: number[]
  offered_service_resource_ids?: number[]
  casting_ids?: number[]
  known_customer_ids?: number[]
  known_employee_ids?: number[]
  known_marketplace_ids?: number[]
  known_offered_service_resource_ids?: number[]
}

export interface PlanningEventsCountPerDayArgs {
  from_date: string
  to_date: string
  'includes[]'?: ('employees' | 'customers')[]
  'marketplace_ids[]'?: number[]
  'employee_ids[]'?: number[]
  'customer_ids[]'?: number[]
  'casting_ids[]'?: number[]
  'offered_service_resource_ids[]'?: number[]
  include_cancelled_events?: boolean
}

export function planningGetBetween (args: PlanningGetBetweenArgs): Promise<PlanningGetBetweenResponse> {
  // use POST instead of GET because the max URL size is 8k by default on nginx, 2k on IE, etc.
  return httpPost(`/${httpGetRoute()}/plannings`, args)
}

export function planningEventsCountPerDay (args: PlanningEventsCountPerDayArgs): Promise<BackofficePlanningEventsCountPerDayResponse> {
  return httpGet(`/${httpGetRoute()}/plannings/events_count_per_day`, args)
}

type EventClockingStatus = 'done' | 'partially_done' | 'missing' | null

export function planningEventClockingStatus (
  event: Record<'start_date' | 'end_date', moment.Moment> & { employee_id: EmployeeBase['id'], clockings?: Pick<ClockingBase, 'clocking_type'>[] },
  market: Pick<SupplierBase, 'clocking_types'>
) : EventClockingStatus {
  if (!('clockings' in event) || !market.clocking_types.length || !event.employee_id) return null
  if (moment(event.end_date).startOf('day').isAfter(moment())) return null

  return market.clocking_types.every(clockingType => event.clockings.some(clocking => clocking.clocking_type === clockingType))
    ? 'done'
    : market.clocking_types.some(clockingType => event.clockings.some(clocking => clocking.clocking_type === clockingType))
    ? 'partially_done'
    : 'missing'

}

export function planningGetMinOpeningAndMaxClosingTime (
  workingDays: WorkingDay[],
  multiOpeningHours: MultiOpeningHours,
  eventsAndOneshotAvailabilities: Record<'start_date' | 'end_date', moment.Moment>[]
) {  
  let minOpeningTime = workingDays.map(dayId => multiOpeningHours[dayId][0][0]).reduce((acc, curr) => Math.min(acc, parseTime(curr)), 24)
  let maxClosingTime = workingDays.map(dayId => multiOpeningHours[dayId][multiOpeningHours[dayId].length - 1][1]).reduce((acc, curr) => Math.max(acc, parseTime(curr)), 0)

  // in case their is no working day, default to a default metric.
  if (workingDays.length === 0) {
    minOpeningTime = 8
    maxClosingTime = 18
  }

  // in some cases, an event could be outside opening hours and extends the rendered range.
  for (const event of eventsAndOneshotAvailabilities) {
    if (!event.start_date.isSame(event.end_date, 'date')) {
      minOpeningTime = 0
      maxClosingTime = 24
      break
    }
    
    minOpeningTime = Math.min(minOpeningTime, event.start_date.hour())
    maxClosingTime = Math.max(maxClosingTime, event.end_date.hour() + event.end_date.minutes() / 60)
  }

  // these metrics are expected to be integers...
  return { minOpeningTime: Math.floor(minOpeningTime), maxClosingTime: Math.ceil(maxClosingTime) }
}

export function planningIsUnavailabilityEvent (event: StoreProps['data']['events'][number]) {
  return event.schedule && scheduleIsScheduleTypeForAnUnavailability(event.schedule.schedule_type)
}
